// ============================================================
// billableChartLayout.ts
// Pure geometry for BillableTimeCard's chart — kept apart so the clipping
// rules (axis ceiling, comparison label, tooltip placement) can be tested
// without a DOM.
//
// viewBox 720×220. L is wide enough for the widest tick ("2h 30m").
// The seven days sit INSIDE the plot: the width [L, W−R] is split into seven
// day slots and each point is centred in its slot — so Monday and Sunday
// are half a slot in from the axes (not on them), and each day's hover
// column is exactly its slot.
// ============================================================
export const W = 720, H = 220, L = 52, R = 12, T = 16, B = 34
export const STEP = (W - L - R) / 7
export const BASE = H - B
// Day i's x (0 = Mon … 6 = Sun) — the point, its label, and its column centre.
export const xOf = (i: number): number => L + STEP / 2 + i * STEP

// Today's marker: the solid day dot (r4) with the gold "Now" ring around it.
export const DOT_R = 4
export const NOW_RING_R = 7.5
export const NOW_RING_STROKE = 2.5
// How far past the plot a marker may reach (the series clip-path margin).
export const PLOT_BLEED = NOW_RING_R + NOW_RING_STROKE / 2 + 1

// Tidy ceilings for the three gridlines (0, ½, max) — every one has a ½ that
// reads cleanly ("15m", "1h 30m", "4h"). Past 24h: multiples of 8h.
const NICE = [0.5, 1, 1.5, 2, 3, 4, 5, 6, 8, 10, 12, 16, 20, 24]
// The peak never sits closer than 5% to the top gridline, so the today ring
// and the dots stay inside the plot.
export const HEADROOM = 1.05

// Axis maximum from the higher of BOTH series (shown week + comparison).
export const axisCeiling = (...series: number[][]): number => {
  const peak = Math.max(0, ...series.flat().filter((v) => Number.isFinite(v)))
  const need = peak * HEADROOM
  for (const n of NICE) if (need <= n + 1e-9) return n
  return Math.ceil(need / 8 - 1e-9) * 8
}

export const yOf = (v: number, max: number): number => T + (BASE - T) * (1 - v / max)

// Every week's line LEADS IN from zero: it starts on the baseline at the
// plot's left edge (the start of Monday), runs up to Monday's point and on
// through the week — both series, however many days they have so far.
// `ys` = the y of each day that has happened (Mon first). The area under it
// closes back down to the baseline under its last point.
const leadInPoints = (ys: number[]): [number, number][] =>
  (ys.length ? [[L, BASE], ...ys.map((y, i): [number, number] => [xOf(i), y])] : [])
export const seriesPath = (ys: number[]): string =>
  leadInPoints(ys).map(([x, y], i) => `${i ? 'L' : 'M'}${x},${y}`).join(' ')
export const seriesArea = (ys: number[]): string | null =>
  (ys.length ? `${seriesPath(ys)} L${xOf(ys.length - 1)},${BASE} Z` : null)

// A label beside a LONE point (a series with only Monday so far): to its
// right, clear of the marker (`clearance` = its outer radius; default the Now
// ring), vertically centred on the point but nudged down below the top / up
// above the baseline so it's never clipped. Always to the right — a lone
// point is Monday, so there's a whole week of room there.
export const VALUE_LABEL_SIZE = 12
export const singlePointLabel = (x: number, y: number, clearance = NOW_RING_R + NOW_RING_STROKE / 2, size = VALUE_LABEL_SIZE): { x: number; y: number } => {
  const lx = x + clearance + 5
  const baselineY = y + size * 0.35
  const minY = T + size * 0.75
  const maxY = BASE - 4
  return { x: lx, y: Math.max(minY, Math.min(baselineY, maxY)) }
}
// The comparison week's marker when it has only one day (Monday) so far: a
// small hollow dot, so its direct label has something to name.
export const CMP_DOT_R = 3.5

// The comparison line's direct label: 9px above its last point, or below it
// when that would run past the top of the chart.
export const CMP_LABEL_SIZE = 11
export const cmpLabelY = (pointY: number): number =>
  pointY - 9 - CMP_LABEL_SIZE < 2 ? pointY + 9 + CMP_LABEL_SIZE : pointY - 9

// Tooltip placement inside a box (the card), all in px relative to the box.
// Above the higher of the day's points; flipped below the lower one when it
// would leave the top; clamped clear of all four edges by `pad`.
export interface TipPlacement { left: number; top: number; below: boolean }
export const placeTooltip = (o: {
  x: number; topY: number; bottomY: number
  tipW: number; tipH: number; boxW: number; boxH: number
  pad?: number; gap?: number
}): TipPlacement => {
  const pad = o.pad ?? 8
  const gap = o.gap ?? 10
  const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(v, hi))
  const left = clamp(o.x - o.tipW / 2, pad, Math.max(pad, o.boxW - o.tipW - pad))
  let top = o.topY - gap - o.tipH
  let below = false
  if (top < pad) {
    top = o.bottomY + gap
    below = true
  }
  top = clamp(top, pad, Math.max(pad, o.boxH - o.tipH - pad))
  return { left, top, below }
}
